"use client";

import { useEffect, useRef } from "react";
import { useAuthStore } from "@/modules/auth/stores/useAuthStore";
import { readSelectedStoreId, useStoreSwitcher, useSwitchStore } from "@/modules/store";
import { nearestStore } from "@/modules/store/lib/nearest-store";
import { useActiveStore } from "@/modules/store/stores/useActiveStore";
import { toast } from "@/shared/components/toast";
import { useAppBoot, useAvailableStores } from "../hooks/useBootstrap";

/** Marca "já perguntei" por navegador: a permissão de localização se pede uma vez só. */
const DONE_KEY = "geo-store-init";

function alreadyAsked(): boolean {
    try {
        return localStorage.getItem(DONE_KEY) !== null;
    } catch {
        return true;
    }
}

function markAsked(): void {
    try {
        localStorage.setItem(DONE_KEY, String(Date.now()));
    } catch {
        // Sem storage (modo privado rígido) a pergunta pode repetir — aceitável.
    }
}

/**
 * Abertura pela loja mais próxima — a opção "usar a localização do cliente"
 * do Comportamento do app (`app_boot.nearest_store_on_init`).
 *
 * Roda UMA vez por navegador, no primeiro acesso, e só quando:
 * - o cliente pode trocar de loja (`can_change_store`) — sem isso a loja é fixa;
 * - ainda não existe loja escolhida (cookie `selected_store`): escolha do
 *   cliente, de outra visita ou do login, vale mais que a geolocalização;
 * - há mais de uma loja com coordenada cadastrada.
 *
 * Com a permissão concedida, a loja mais próxima vira a ativa pelo mesmo
 * caminho do seletor (`useSwitchStore`: cookie + estado + refresh), e o
 * seletor obrigatório, se estiver aberto, fecha. Negada ou indisponível,
 * segue o fluxo de sempre — e não pergunta de novo.
 */
export function GeoStoreInit() {
    const boot = useAppBoot();
    const stores = useAvailableStores();
    const authStatus = useAuthStore((s) => s.status);
    const storeReady = useActiveStore((s) => s.ready);
    const switchStore = useSwitchStore();
    const started = useRef(false);

    const enabled = Boolean(boot?.can_change_store) && Boolean(boot?.nearest_store_on_init);

    useEffect(() => {
        if (!enabled || started.current) return;
        if (authStatus === "loading" || !storeReady) return;
        if (typeof navigator === "undefined" || !navigator.geolocation) return;
        if (alreadyAsked() || readSelectedStoreId() !== null) return;
        if (stores.filter((store) => store.latitude !== "" && store.longitude !== "").length < 2) return;

        started.current = true;

        navigator.geolocation.getCurrentPosition(
            (position) => {
                markAsked();
                const nearest = nearestStore(stores, position.coords.latitude, position.coords.longitude);
                if (!nearest) return;

                const current = useActiveStore.getState().storeId;
                if (nearest.store.store_id === current) return;

                useStoreSwitcher.getState().close();
                switchStore(nearest.store.store_id);
                toast.info(`Atendendo pela loja mais próxima: ${nearest.store.description}`);
            },
            () => {
                // Negou ou falhou: não insiste. O seletor continua disponível.
                markAsked();
            },
            { timeout: 10_000, maximumAge: 300_000 }
        );
    }, [enabled, authStatus, storeReady, stores, switchStore]);

    return null;
}
